import { useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { nextTaskStatus } from "../lib/tasks";

// The task actions every screen offers (status cycle, quick checkbox, completion with comment, postpone, co-responsable,
// delete), written once. The page keeps its own list: `onUpdated(task)` after any change, `onDeleted(task)` after a delete,
// and `onFormCompleted()` when a completion came from the edit form (which then closes).
export default function useTaskActions({ onUpdated, onDeleted, onFormCompleted }) {
  const [completing, setCompleting] = useState(null);
  const [deletingTask, setDeletingTask] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const patch = async (task, body, success, fallback) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, body);
      onUpdated(data);
      if (success) toast.success(success);
      return true;
    } catch (e) {
      toast.error(errorMessage(e, fallback));
      return false;
    }
  };

  const markDone = (task) => setCompleting({ task });

  const cycleStatus = (task) => {
    const next = nextTaskStatus(task.status || (task.done ? "termine" : "a_faire"));
    if (next === "termine") return markDone(task);
    return patch(task, { status: next }, null, "Impossible de mettre à jour le statut.");
  };

  // The quick checkbox reopens directly; closing goes through the same confirmation as every other completion path.
  const toggleDone = (task) => {
    if (!task.done) return markDone(task);
    return patch(task, { status: "a_faire" }, null, "Impossible de mettre à jour le statut.");
  };

  const confirmComplete = async (comment, performer) => {
    if (!completing) return;
    const { task, payload } = completing;
    const body = { ...(payload || { status: "termine" }), completion_comment: comment, ...(performer ? { completion_performed_by: performer } : {}) };
    if (await patch(task, body, "Tâche marquée terminée", "Impossible de terminer la tâche.")) {
      if (payload) onFormCompleted?.();
      setCompleting(null);
    }
  };

  // Kanban moves: a plain status change, or a cancellation that goes through the reason dialog (the API refuses one without).
  const changeStatus = (task, status) => patch(task, { status }, null, "Impossible de déplacer la tâche.");

  const confirmCancel = async (reason) => {
    if (!cancelling) return false;
    const done = await patch(cancelling.task, { status: "annulee", cancel_reason: reason }, "Tâche annulée", "Impossible d'annuler la tâche.");
    if (done) setCancelling(null);
    return done;
  };

  const postpone = (task, dueDate) => patch(task, { due_date: dueDate || null }, "Échéance mise à jour", "Impossible de reporter la tâche.");

  const addAssignee = (task, userId) =>
    patch(task, { assigned_to: [...new Set([...(task.assigned_to || []), userId])] }, "Responsable ajouté", "Impossible d'ajouter ce responsable.");

  const confirmDelete = async (reason) => {
    const task = deletingTask;
    if (!task) return;
    try {
      await api.delete(`/tasks/${task.id}`, { data: { reason } });
      onDeleted(task);
      toast.success("Tâche mise à la corbeille");
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de supprimer la tâche."));
    } finally {
      setDeletingTask(null);
    }
  };

  return {
    completing, setCompleting, deletingTask, setDeletingTask, cancelling, setCancelling,
    markDone, cycleStatus, toggleDone, confirmComplete, changeStatus, confirmCancel, postpone, addAssignee, confirmDelete,
  };
}
